import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import type { RootState } from "../../../store/store";

import {
  createPayrollPeriodApi,
  DpPayrollKind,
  DpPayrollPeriodListItem,
  formatPayrollCompetence,
  listPayrollPeriodsApi,
  PAYROLL_KIND_LABELS,
} from "../api/payrollApi";
import { formatCurrency, formatDateOnly, MONTH_LABELS } from "../utils/format";
import { DP_PAGE, DP_PAYROLL_PERIOD_PAGE, DP_SETTINGS_PAGE } from "../routes";
import { useGoBack } from "../../../hooks/useGoBack";
import "./Dp.css";
import "./DpEco.css";

/** O típico é abrir a competência do mês anterior (o salário sai no 5º dia útil). */
const previousCompetence = () => {
  const now = new Date();
  const month = now.getMonth();
  return month === 0 ? { year: now.getFullYear() - 1, month: 12 } : { year: now.getFullYear(), month };
};

export default function PayrollPeriodsPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(DP_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const currentYear = new Date().getFullYear();
  const yearOptions = useMemo(
    () => Array.from({ length: currentYear + 1 - 2024 + 1 }, (_, i) => currentYear + 1 - i),
    [currentYear]
  );

  const [year, setYear] = useState(currentYear);
  const [periods, setPeriods] = useState<DpPayrollPeriodListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(() => {
    const c = previousCompetence();
    return { year: String(c.year), month: String(c.month), kind: "MENSAL" as DpPayrollKind };
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      setPeriods(await listPayrollPeriodsApi(token, year));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar as competências.");
    } finally {
      setLoading(false);
    }
  }, [token, year]);

  useEffect(() => {
    load();
  }, [load]);

  const open = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setSaving(true);
    try {
      const monthly = form.kind === "MENSAL";
      const created = await createPayrollPeriodApi(token, {
        year: Number(form.year),
        kind: form.kind,
        ...(monthly ? { month: Number(form.month) } : {}),
      });
      toast.success(monthly ? "Competência aberta. Clique em Calcular." : "Parcela do 13º aberta. Clique em Calcular.");
      navigate(DP_PAYROLL_PERIOD_PAGE(created.id), { replace: true });
    } catch (error: any) {
      toast.error(error?.message || "Erro ao abrir a competência.");
    } finally {
      setSaving(false);
    }
  };

  if (!token) return null;

  return (
    <div className="dpLayout">
      <main className="dpMain">
        <div className="dpContainer">
          <EcoPageHeader
            crumbs={[{ label: "Departamento Pessoal", onClick: () => navigate(DP_PAGE()) }, { label: "Folha" }]}
            title="Folha para conferência"
            subtitle="O pagamento de cada colaborador calculado pelo sistema, para conferir com a folha do contador."
            onBack={goBack}
            backLabel="Voltar para o DP"
            actions={
              <button type="button" className="ecoBtn" onClick={() => navigate(`${DP_SETTINGS_PAGE()}?aba=tabelas`)}>
                <EcoIcon name="gear" size={18} />
                Tabelas e política
              </button>
            }
          />

          <div className="dpControls">
            <select className="dpSelect" aria-label="Ano" value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <button className="dpBtn dpBtn--primary" onClick={() => setModal(true)}>
              Abrir competência ou 13º
            </button>
          </div>

          <p className="dpNotice">
            Não é holerite nem vai ao eSocial: a folha oficial continua sendo a do contador. Tabelas de INSS e IR,
            política de pagamento e rubricas ficam em Cadastros.
          </p>

          {loading ? (
            <div className="dpEmpty">Carregando...</div>
          ) : (
            <div className="dpTableWrapper">
              <table className="dpTable">
                <thead>
                  <tr>
                    <th>Competência</th>
                    <th>Tipo</th>
                    <th>Situação</th>
                    <th>Colaboradores</th>
                    <th>Bruto</th>
                    <th>Líquido</th>
                    <th>Pagamento</th>
                    <th>Calculado em</th>
                    <th style={{ width: 110 }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {periods.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="dpEmpty">
                        Nenhuma competência aberta em {year}.
                      </td>
                    </tr>
                  ) : (
                    periods.map((p) => (
                      <tr key={p.id}>
                        <td>
                          <strong>{formatPayrollCompetence(p)}</strong>
                        </td>
                        <td>{PAYROLL_KIND_LABELS[p.kind] ?? "Folha mensal"}</td>
                        <td>
                          <span className={`dpBadge ${p.status === "FECHADO" ? "dpBadge--gray" : "dpBadge--blue"}`}>
                            {p.status === "FECHADO" ? "Fechada" : "Aberta"}
                          </span>
                        </td>
                        <td>{p.totals.contracts}</td>
                        <td>{formatCurrency(p.totals.gross)}</td>
                        <td>{formatCurrency(p.totals.net)}</td>
                        <td>{formatDateOnly(p.paymentDate)}</td>
                        <td>{p.calculatedAt ? new Date(p.calculatedAt).toLocaleString("pt-BR") : "—"}</td>
                        <td>
                          <button
                            className="dpBtn dpBtn--secondary dpBtn--small"
                            onClick={() => navigate(DP_PAYROLL_PERIOD_PAGE(p.id))}
                          >
                            Abrir
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {modal ? (
        <div className="dpModalOverlay">
          <div className="dpModal">
            <header className="dpModal__header">
              <h2 className="dpModal__title">Abrir competência</h2>
              <button className="dpModal__close" onClick={() => setModal(false)}>
                &times;
              </button>
            </header>
            <form onSubmit={open}>
              <div className="dpModal__body">
                <div className="dpFormGroup">
                  <label htmlFor="payrollKind">Tipo *</label>
                  <select
                    id="payrollKind"
                    className="dpSelect"
                    value={form.kind}
                    onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value as DpPayrollKind }))}
                    disabled={saving}
                  >
                    {(Object.keys(PAYROLL_KIND_LABELS) as DpPayrollKind[]).map((kind) => (
                      <option key={kind} value={kind}>
                        {PAYROLL_KIND_LABELS[kind]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={form.kind === "MENSAL" ? "dpFormGrid dpFormGrid--two" : "dpFormGrid"}>
                  {form.kind === "MENSAL" ? (
                    <div className="dpFormGroup">
                      <label htmlFor="payrollMonth">Mês *</label>
                      <select
                        id="payrollMonth"
                        className="dpSelect"
                        value={form.month}
                        onChange={(e) => setForm((f) => ({ ...f, month: e.target.value }))}
                        disabled={saving}
                      >
                        {MONTH_LABELS.map((label, i) => (
                          <option key={label} value={i + 1}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : null}
                  <div className="dpFormGroup">
                    <label htmlFor="payrollYear">Ano *</label>
                    <select
                      id="payrollYear"
                      className="dpSelect"
                      value={form.year}
                      onChange={(e) => setForm((f) => ({ ...f, year: e.target.value }))}
                      disabled={saving}
                    >
                      {yearOptions.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <p className="dpFormHint" style={{ margin: 0 }}>
                  {form.kind === "MENSAL"
                    ? "As datas do adiantamento e do pagamento vêm da política de pagamento e podem ser ajustadas depois."
                    : form.kind === "DECIMO_TERCEIRO_1"
                      ? "1ª parcela: metade do 13º, sem INSS e sem IR, paga até 30/11 (dia útil anterior se cair em fim de semana ou feriado). A data pode ser ajustada depois."
                      : "2ª parcela: o 13º inteiro menos a 1ª, com INSS e IR separados do salário, paga até 20/12. Abra e feche a 1ª parcela antes."}
                </p>
              </div>
              <footer className="dpModal__footer">
                <button type="button" className="dpBtn dpBtn--secondary" onClick={() => setModal(false)} disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
                  Abrir
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
